import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { LoginService } from "./login.service";
import { ReconnectingSocket } from "./reconnecting-socket";

export interface SiteClinic { GID: number; ClinicTitle: string; }
export interface SiteCount { GID: number; ClinicTitle: string; Waiting: number; InProgress: number; SignedToday: number; }

// Sites partnership — interpreting doctor's cross-clinic state. The TodayList
// (patients.component) uses this to aggregate across the doctor's sites. Inert
// for everyone else (/api/sites returns [] → crossMode never turns on).
@Injectable({ providedIn: "root" })
export class SitesService {
  private _sites: SiteClinic[] | null = null;
  public byId: { [gid: number]: SiteClinic } = {};

  // Global clinic switcher: the doctor's current "working context" — a specific
  // site GID (session is actually switched via silentSetClinic) or 'all' (session
  // GID is left untouched; read endpoints that support it aggregate server-side,
  // and new-patient creation is blocked — there's no single clinic to write into).
  public activeSite: number | "all" | null = null;
  // Emits whenever activeSite changes (including the initial default), so views
  // with their own independent site filter (TodayList) can stay in sync with the
  // global switcher without polling it.
  public activeSiteChanged$ = new Subject<number | "all">();

  // Live-update fan-in: any subscribed site's awl socket fires this.
  public changed$ = new Subject<void>();
  private sockets: ReconnectingSocket[] = [];

  constructor(private http: HttpClient, private ls: LoginService) {}

  get hasSites(): boolean { return !!this._sites && this._sites.length > 0; }
  get sites(): SiteClinic[] { return this._sites || []; }

  // Fetch the caller's site clinics once per session. Returns [] for non-site users.
  // Defaults activeSite to the first site — never "all" — and genuinely switches
  // the session to it (not just a cosmetic default), so the dropdown, the session
  // GID, and everything derived from it (TodayList included) all agree from the
  // start. Switching to "all" afterward is an explicit action.
  loadSites(force = false) {
    return new Promise<SiteClinic[]>((resolve) => {
      if (this._sites !== null && !force) { resolve(this._sites); return; }
      this.http.get<SiteClinic[]>("/api/sites").subscribe({
        next: async (data) => {
          this._setSites(data || []);
          if (this.activeSite === null && this._sites.length) {
            await this.setActiveSite(this._sites[0].GID);
          }
          resolve(this._sites);
        },
        error: () => { this._setSites([]); resolve([]); },
      });
    });
  }

  private _setSites(list: SiteClinic[]) {
    this._sites = list;
    this.byId = {};
    for (const s of list) this.byId[s.GID] = s;
  }

  // Switch the global clinic context. A specific GID actually re-scopes the
  // session (silentSetClinic) so every session-GID-scoped feature (search,
  // creation) follows; "all" leaves the session alone and is opt-in per feature.
  async setActiveSite(site: number | "all") {
    if (site === "all") {
      this.activeSite = "all";
      this.activeSiteChanged$.next(site);
      return;
    }
    await this.ls.silentSetClinic(site);
    this.activeSite = site;
    this.activeSiteChanged$.next(site);
  }

  // Block-and-confirm instead of silently switching. A patient/exam view fires
  // many independently clinic-scoped calls on mount (gallery, DICOM, print
  // archive, patient/exam lookup) — each trusts the session GID on its own, so
  // silently switching then immediately navigating proved too fragile to fully
  // audit (one more call site kept turning up broken). Instead: check once,
  // and if the record's GID doesn't match the active clinic (including "all"
  // mode, which has no single active clinic to match), surface an explicit
  // confirm step instead of proceeding — this also guarantees the switch fully
  // completes (a whole separate user action) before anything else can fire.
  // `anchor` is the element the user actually clicked (row/link/button) — kept
  // so the confirm prompt can float next to it (see SiteSwitchPromptComponent)
  // instead of appearing as a page-top banner disconnected from the click.
  public pendingSwitch: { gid: number; siteName: string; proceed: () => void; anchor?: HTMLElement | null } | null = null;
  public switchError: string | null = null;

  requestOpen(gid: number, proceed: () => void, anchor?: HTMLElement | null) {
    if (!gid) { proceed(); return; }
    // Coerce both sides — GID can arrive as either a number or a numeric string
    // depending on the query/driver path it came through, and a strict !== would
    // wrongly treat those as a mismatch and block silently.
    if (this.activeSite !== "all" && Number(gid) === Number(this.ls.ClinicID)) {
      proceed();
      return;
    }
    this.switchError = null;
    this.pendingSwitch = {
      gid: Number(gid),
      siteName: this.byId[gid]?.ClinicTitle || String(gid),
      proceed,
      anchor: anchor || null,
    };
  }

  async confirmSwitch() {
    if (!this.pendingSwitch) return;
    const { gid, proceed } = this.pendingSwitch;
    try {
      await this.setActiveSite(gid);
      this.pendingSwitch = null;
      proceed();
    } catch {
      this.switchError = "Could not switch clinic. Please try again.";
    }
  }

  cancelSwitch() {
    this.pendingSwitch = null;
    this.switchError = null;
  }

  getCounts() { return this.http.get<SiteCount[]>("/api/sitescounts"); }

  // Open one existing awl socket per site (no server change). Any message → changed$.
  openSockets(gids: number[]) {
    this.closeSockets();
    const base = (window.location.protocol == "https:" ? "wss://" : "ws://") +
      window.location.hostname + (window.location.port ? ":" + window.location.port : "") +
      "/api/awlsocket/";
    for (const gid of gids) {
      this.sockets.push(new ReconnectingSocket(base + gid, () => this.changed$.next()));
    }
  }

  closeSockets() {
    for (const ws of this.sockets) { try { ws.close(); } catch {} }
    this.sockets = [];
  }

  reset() {
    this._sites = null;
    this.byId = {};
    this.closeSockets();
    this.activeSite = null;
    this.pendingSwitch = null;
    this.switchError = null;
  }
}
